import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useCurrentEmployee } from '../hooks/useQueries';
import { clearImpersonationAndReload, getImpersonatedEmployeeId } from '../utils/impersonation';

const AuthContext = createContext(null);

const getPermissionFromObject = (obj, permission) => {
  if (!obj || typeof obj !== 'object') return false;
  if (obj['*'] === true) return true;

  const parts = String(permission).split('.').filter(Boolean);
  let cursor = obj;
  for (const part of parts) {
    if (!cursor || typeof cursor !== 'object') return false;
    cursor = cursor[part];
  }
  return cursor === true;
};

const normalizePermissionName = (permission) => {
  const value = String(permission || '').trim();
  if (!value) return value;
  if (value === 'manage_employees') return 'manage_all_employees';
  return value;
};

export function AuthProvider({ children }) {
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch
  } = useCurrentEmployee({ enabled: true });

  const employee = data?.employee || null;
  const role = data?.role || null;
  // The real person, while the employee above is somebody they are
  // impersonating; null otherwise.
  const impersonatedBy = data?.impersonated_by || null;
  const isResolving = isLoading || (!data && !error);

  /*
    The server refused the impersonation - the permission was taken away, the
    target was deactivated, or the target has since been given more access than
    the impersonator. Every request would fail the same way, so drop it and
    come back as the real person rather than leave them stuck on an error.
  */
  const impersonationRefused = error?.status === 403
    && String(error?.code || '').startsWith('impersonation_')
    && Boolean(getImpersonatedEmployeeId());
  useEffect(() => {
    if (impersonationRefused) clearImpersonationAndReload();
  }, [impersonationRefused]);

  const value = useMemo(() => {
    const permissions =
      employee?.permission_grants && typeof employee.permission_grants === 'object' && !Array.isArray(employee.permission_grants)
        ? employee.permission_grants
        : {};
    const hasPermissionDirect = (permission) => getPermissionFromObject(permissions, normalizePermissionName(permission));

    const isAdmin = () => hasPermissionDirect('*');
    const isApprover = () => {
      return hasPermissionDirect('approve_time_off');
    };
    const isFinance = () => {
      return hasPermissionDirect('approve_expenses');
    };
    const hasPermission = (permission) => hasPermissionDirect(permission);

    return {
      employee,
      role,
      impersonatedBy,
      isImpersonating: Boolean(impersonatedBy),
      isLoading,
      isFetching,
      isResolving,
      error,
      refresh: refetch,
      isAdmin,
      isApprover,
      isFinance,
      hasPermission,
      permissions,
    };
  }, [employee, role, impersonatedBy, isLoading, isFetching, isResolving, error, refetch]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
